import React, { Component } from 'react';
import { connect } from 'react-redux';
import { isEmpty, get, omit, find, flow } from 'lodash-es';
/* Models */
import { CarBuyingModel, PATHS } from 'client/data/models/car-buying';
import { VisitorModel } from 'client/data/models/visitor';
import { InventoryModel } from 'client/data/models/inventory';
import { CalculatorsModel, getStoredSelectionsPath } from 'client/data/models/calculators';
import { RequestModel } from 'client/data/models/request';
/* Utils */
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import {
  CALCULATION_API_ERROR_FIELD,
  TERMS_API_ERROR_FIELD,
  getMultiMonthlyPayments,
  getDefaultOffer,
  getCalculatorInitialState,
  getVehiclePricesNewFields,
} from 'site-modules/shared/utils/car-buying/monthly-payment-calculator-call';
import {
  getCalculatorConfig,
  getCalculatorSelections,
  isCreditScoreValid,
} from 'site-modules/shared/utils/car-buying/calculator-util';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import {
  fetchLoanInventoryOffers,
  fetchLoanOffersTermsApi,
  getLeaseOffersWithoutIncentives,
  getOffers,
  adjustOffersWithIncentives,
} from 'site-modules/shared/utils/car-buying/digital-retail-payment-service';
import { buildOfferFromInventory } from 'site-modules/shared/utils/car-buying/build-offer-from-inventory';
import { resolveLeaseCreditScore } from 'site-modules/shared/utils/calculator/calculator-helper';
import {
  getDealsByType,
  getDealPrices,
} from 'site-modules/shared/components/car-buying/calculate-deal-payments-by-type/calculate-deal-payments-by-type-utils';
import {
  mapLoanAprsToLoanTermsFormat,
  mapLoanOffersToRetailModel,
} from 'site-modules/shared/utils/car-buying/map-to-retail-model';
import { buildConfigurationTmvSimplifiedWithOptionsPath, BuildPriceModel } from 'client/data/models/build-price';
import { withIncentives } from 'site-modules/shared/components/inventory/with-incentives/with-incentives';
import { isTmvPriceAvailable } from 'site-modules/shared/utils/inventory/price-analysis';
/* HOCs */
import { withMetrics } from 'client/utils/metrics-hoc';
/* Constants */
import { LEASE, PURCHASE } from 'client/site-modules/shared/constants/car-buying';
import { DEFAULT_LEASE_ANNUAL_MILEAGE } from 'site-modules/shared/constants/calculator/calculator';

const EMPTY_CONFIG = { mileagesMapping: {}, termsMapping: {} };

/**
 * Calculates deals by type (Great, Good, Fair)
 * @example
 * // wrap component into HOC
 * const Component = calculateDealPaymentsByType(WrappedComponent)
 * // pass required prop to connected component
 * <Component vin={vin} isPurchase={isPurchase} />
 * // pass dealerZip if needed (e.g. if it's not set before)
 * <Component vin={vin} isPurchase={isPurchase} dealerZip={dealerZip} />
 * // pass `isPurchaseOnly` prop to disable lease calculations
 * <Component vin={vin} isPurchase isPurchaseOnly />
 * @param WrappedComponent
 * @return {CalculateDealPaymentByType}
 */
export function calculateDealPaymentsByType(WrappedComponent) {
  class CalculateDealPaymentByType extends Component {
    constructor(props) {
      super(props);

      this.state = {
        deals: { [PURCHASE]: [], [LEASE]: [] },
        isPending: true,
        basePurchaseDeal: {},
        calculatorConfigs: { [PURCHASE]: EMPTY_CONFIG, [LEASE]: EMPTY_CONFIG },
        digitalRetailOffers: { [PURCHASE]: null, [LEASE]: null },
      };

      this.isInitialDataLoaded = false;
      this.prevCalculatorSelections = { [PURCHASE]: null, [LEASE]: null };
    }

    componentDidMount() {
      this.togglePendingState(false);
    }

    async componentDidUpdate(prevProps) {
      const {
        vinData,
        storedZipCode,
        selectedLoanIncentives,
        selectedLeaseIncentives,
        storedLoanSelections,
        storedLeaseSelections,
        isPurchase,
      } = this.props;

      if (!isTmvPriceAvailable(vinData)) {
        return;
      }

      const calculatorSelections = this.getCalculatorSelections();
      const activeCalculatorTab = this.getActiveCalculatorTab();

      const { creditScore } = calculatorSelections[activeCalculatorTab];

      // if initial data loaded it means that we can watch for properties that can be changed by user
      if (this.isInitialDataLoaded) {
        const { creditScore: prevCreditScore } = this.prevCalculatorSelections[activeCalculatorTab];

        const isZipChanged = storedZipCode !== prevProps.storedZipCode;
        const isCreditScoreChanged = creditScore !== prevCreditScore;

        const currentIncentives = isPurchase ? selectedLoanIncentives : selectedLeaseIncentives;
        const prevIncentives = isPurchase ? prevProps.selectedLoanIncentives : prevProps.selectedLeaseIncentives;

        // recalculate offers and payments if zip, incentives, or credit score changed
        // otherwise recalculate payments if user selections (down payment, trade-in, etc.) changed
        if (
          isCreditScoreValid(creditScore) &&
          (isZipChanged || isCreditScoreChanged || (isPurchase && prevIncentives !== currentIncentives))
        ) {
          this.prevCalculatorSelections = calculatorSelections;
          const offersResponse = await this.getOffersResponse();
          const digitalRetailOffers = await this.calculateDigitalRetailOffers(offersResponse);
          await this.calculatePayments({ digitalRetailOffers });
          return;
        }

        if (this.state.digitalRetailOffers && !isPurchase && currentIncentives !== prevIncentives) {
          const digitalRetailOffers = {
            ...this.state.digitalRetailOffers,
            [LEASE]:
              this.state.digitalRetailOffers[LEASE] &&
              adjustOffersWithIncentives({
                offers: this.state.digitalRetailOffers[LEASE],
                incentives: selectedLeaseIncentives,
              }),
          };

          this.setState({
            digitalRetailOffers,
          });

          this.calculatePayments({ digitalRetailOffers });
        }
      } else {
        const waitForStoredCalculatorData = !(storedLoanSelections && storedLeaseSelections);
        // calculate offers and payments only when all data loaded and available
        if (!waitForStoredCalculatorData) {
          this.isInitialDataLoaded = true;
          this.prevCalculatorSelections = calculatorSelections;
          if (isCreditScoreValid(creditScore)) {
            const offersResponse = await this.getOffersResponse({ useSyntheticLease: true });
            const digitalRetailOffers = await this.calculateDigitalRetailOffers(offersResponse);
            await this.calculatePayments({ digitalRetailOffers });
          }
        }
      }
    }

    getDealsFromTmv = () => {
      const { totalWithOptionsTMV, selectedLoanIncentives: incentives, vinData } = this.props;
      const purchase = [];
      if (totalWithOptionsTMV) {
        const maxGoodPrice = totalWithOptionsTMV;
        const maxGreatPrice = Number((maxGoodPrice * 0.97).toFixed(2));
        const maxFairPrice = Number((maxGoodPrice * 1.03).toFixed(2));
        purchase.push({
          dealOffer: { dealType: 'great', sellingPrice: maxGreatPrice, incentives },
          inventory: vinData,
        });
        purchase.push({ dealOffer: { dealType: 'good', sellingPrice: maxGoodPrice, incentives }, inventory: vinData });
        purchase.push({ dealOffer: { dealType: 'fair', sellingPrice: maxFairPrice, incentives }, inventory: vinData });
      }
      return { [PURCHASE]: purchase, [LEASE]: [] };
    };

    getActiveCalculatorTab() {
      return this.props.isPurchase ? PURCHASE : LEASE;
    }

    /**
     * Checks if selected vehicles were submitted via ULF.
     * @returns {boolean}
     */
    getIsSubmitted = () => {
      const { showSubmitLoadingIndicator, submittedLeadTargetsData } = this.props;

      return !showSubmitLoadingIndicator && !isEmpty(submittedLeadTargetsData && submittedLeadTargetsData.leadIds);
    };

    /**
     * Checks if vehicle is used or forced to used.
     * @return {boolean}
     */
    getIsUsed = () => {
      const { isUsed: isUsedProp, vinData } = this.props;

      return !!isUsedProp || isUsed(get(vinData, 'type', ''));
    };

    /**
     * Checks if lease calculations disabled.
     * @return {boolean}
     */
    getIsLeaseDisabled = () => {
      const { isPurchaseOnly, vinData } = this.props;

      return isPurchaseOnly || this.getIsUsed() || !get(vinData, 'vehicleInfo.styleInfo.styleId');
    };

    /**
     * Gets default calculator selections for lease and loan.
     * @return {object}
     */
    getCalculatorSelections = () => {
      const { storedLeaseSelections, storedLoanSelections, vinData, defaultLoanTerm72mo } = this.props;

      return getCalculatorSelections({
        storedLeaseSelections,
        storedLoanSelections,
        isUsed: this.getIsUsed(),
        vehicle: vinData,
        defaultLoanTerm72mo,
      });
    };

    /**
     * Gets error message for selected calculator tab.
     * @returns {string}
     */
    getErrorMessage = () => {
      const isPurchase = this.props.isPurchase;

      return (
        this.getErrorMessageByField({ field: TERMS_API_ERROR_FIELD, isPurchase }) ||
        this.getErrorMessageByField({ field: CALCULATION_API_ERROR_FIELD, isPurchase })
      );
    };

    /**
     * Gets error message by passed field.
     * @param {string} field
     * @param {boolean} isPurchase
     * @returns {string|undefined}
     */
    getErrorMessageByField = ({ field, isPurchase }) => this[isPurchase ? `loan${field}` : `lease${field}`];

    /**
     * Gets calculated offers.
     * @param {object[]} filteredIncentives
     * @param {string} offerType
     * @return {Promise<null|object[]>}
     */
    async getOffers({ filteredIncentives, offerType }) {
      const {
        vinData,
        storedZipCode,
        apiMetrics: { pageName, venomVersion, pageCategory, xTraceId },
        visitorId,
        protocol,
        hostName,
        url,
        useGraphQlForLoanOffers,
      } = this.props;

      const {
        dealerInfo: {
          address: { zip: dealerZipCode },
        },
        vehicleInfo: {
          styleInfo: { styleId, year },
          mileage,
        },
        type: vehicleType,
        vin,
      } = vinData;

      const isPurchase = offerType === PURCHASE;

      try {
        const offers = await getOffers(
          {
            creditScore: this.getCalculatorSelections()[offerType].creditScore,
            styleId,
            vin,
            zipCode: isPurchase ? storedZipCode : dealerZipCode,
            filteredIncentives,
            year,
            mileage,
            selectedTerm: this.getCalculatorSelections()[PURCHASE].term,
          },
          {
            isPurchase,
            visitorId,
            pageName,
            venomVersion,
            isUsed: isUsed(vehicleType),
            pageCategory,
            xTraceId,
            protocol,
            hostName,
            url,
            loanOffersFetcher: useGraphQlForLoanOffers ? fetchLoanInventoryOffers : fetchLoanOffersTermsApi,
            leaseOffersFetcher: getLeaseOffersWithoutIncentives,
          }
        );

        this.setErrorMessage({ field: TERMS_API_ERROR_FIELD, clear: true, isPurchase });

        return offers;
      } catch (error) {
        this.setErrorMessage({ field: TERMS_API_ERROR_FIELD, error, isPurchase });

        return null;
      }
    }

    /**
     * Sets error message.
     * @param {string} field
     * @param {object} error
     * @param {boolean} clearError
     * @param {boolean} isPurchase
     */
    setErrorMessage = ({ field, error, clear, isPurchase }) => {
      const fieldName = isPurchase ? `loan${field}` : `lease${field}`;
      const newErrorMsg = (error && error.message) || 'Unclassified error';

      this[fieldName] = clear ? undefined : newErrorMsg;
    };

    getOffersResponse = async ({ useSyntheticLease } = {}) => {
      const { selectedLoanIncentives, selectedLeaseIncentives, vinData, storedLeaseSelections } = this.props;

      const isLeaseDisabled = this.getIsLeaseDisabled();
      const ingestionCreditScore = resolveLeaseCreditScore();
      const storedCreditScore = storedLeaseSelections?.creditScore;
      const isStoredCreditScoreMismatch = !!storedCreditScore && storedCreditScore !== ingestionCreditScore;

      const getLeaseOffers = () => {
        if (isLeaseDisabled) {
          return Promise.reject(new Error('Lease disabled'));
        }

        if (useSyntheticLease && !isStoredCreditScoreMismatch) {
          const syntheticOffer = buildOfferFromInventory(vinData);
          if (syntheticOffer) {
            return Promise.resolve(syntheticOffer);
          }
        }

        return this.getOffers({
          filteredIncentives: selectedLeaseIncentives,
          offerType: LEASE,
        });
      };

      // get offers for purchase and lease based on available incentives
      return Promise.allSettled([
        this.getOffers({
          filteredIncentives: selectedLoanIncentives,
          offerType: PURCHASE,
        }),
        getLeaseOffers(),
      ]);
    };

    /**
     * Toggles pending state (on/off loading indicator).
     * @param {boolean} isPending
     */
    togglePendingState = isPending => this.setState({ isPending });

    /**
     * Calculates offers for lease and loan.
     * @return {Promise<*>}
     */
    calculateDigitalRetailOffers = async offersResponse => {
      this.togglePendingState(true);
      const { selectedLoanIncentives, selectedLeaseIncentives, useGraphQlForLoanOffers } = this.props;

      const [{ value: purchaseOffersResponseValue }, { value: leaseDigitalRetailOffers }] = offersResponse;

      const purchaseDigitalRetailOffers =
        useGraphQlForLoanOffers && purchaseOffersResponseValue
          ? {
              annualMileage: {
                [DEFAULT_LEASE_ANNUAL_MILEAGE]: mapLoanOffersToRetailModel(
                  mapLoanAprsToLoanTermsFormat(purchaseOffersResponseValue),
                  []
                ),
              },
            }
          : purchaseOffersResponseValue;

      const digitalRetailOffers = {
        [PURCHASE]:
          purchaseDigitalRetailOffers &&
          adjustOffersWithIncentives({
            offers: purchaseDigitalRetailOffers,
            incentives: selectedLoanIncentives,
          }),
        [LEASE]:
          leaseDigitalRetailOffers &&
          adjustOffersWithIncentives({ offers: leaseDigitalRetailOffers, incentives: selectedLeaseIncentives }),
      };

      this.setState({
        digitalRetailOffers,
        calculatorConfigs: {
          [PURCHASE]: purchaseDigitalRetailOffers
            ? getCalculatorConfig(purchaseDigitalRetailOffers, { key: 'value', isPurchase: true })
            : EMPTY_CONFIG,
          [LEASE]: leaseDigitalRetailOffers
            ? getCalculatorConfig(leaseDigitalRetailOffers, { key: 'value', isPurchase: false })
            : EMPTY_CONFIG,
        },
        purchaseOffers: purchaseOffersResponseValue,
      });

      return digitalRetailOffers;
    };

    /**
     * Calculates payments for lease and loan.
     * @return {Promise<void>}
     */
    calculatePayments = async ({ digitalRetailOffers = [] }) => {
      if (!this.state.isPending) this.togglePendingState(true);

      try {
        const {
          storedZipCode,
          visitorId,
          apiMetrics: { pageName, venomVersion },
          vinData,
          photoUrl,
          isLeasePaymentFocused,
          primaryLoanIncentives,
          useGraphQlForLoanOffers,
          selectedLoanIncentives,
          selectedLeaseIncentives,
          isPurchase,
        } = this.props;
        const isLeaseDisabled = this.getIsLeaseDisabled() || !digitalRetailOffers[LEASE];

        const defaultPurchaseOffer = getDefaultOffer(digitalRetailOffers[PURCHASE], true);
        const defaultLeaseOffer = isLeaseDisabled ? null : getDefaultOffer(digitalRetailOffers[LEASE], false);

        const calculatorSelections = this.getCalculatorSelections();

        const purchaseCalculatorValues = getCalculatorInitialState(
          digitalRetailOffers[PURCHASE],
          calculatorSelections[PURCHASE],
          defaultPurchaseOffer,
          true
        );
        const leaseCalculatorValues = isLeaseDisabled
          ? null
          : getCalculatorInitialState(
              digitalRetailOffers[LEASE],
              calculatorSelections[LEASE],
              defaultLeaseOffer,
              false
            );

        const prices = getDealPrices({
          vehicle: vinData,
          isLeasePaymentFocused,
          isPurchase,
          primaryIncentives: primaryLoanIncentives,
          selectedIncentives: isLeasePaymentFocused ? selectedLeaseIncentives : selectedLoanIncentives,
        })
          .map(({ price }) => price)
          .join(',');

        const [purchasePaymentsResponse, leasePaymentsResponse] = await Promise.allSettled([
          getMultiMonthlyPayments(
            {
              offer: digitalRetailOffers[PURCHASE],
              zipCode: storedZipCode,
              selectedTerm: purchaseCalculatorValues.term,
              selectedTradeIn: purchaseCalculatorValues.tradeIn,
              selectedMileage: purchaseCalculatorValues.annualMileage,
              selectedDownPayment: purchaseCalculatorValues.downPayment,
              prices,
              vehicle: vinData,
              vehiclePrices: getVehiclePricesNewFields(vinData),
              useGraphQlForLoanOffers,
            },
            {
              visitorId,
              pageName,
              venomVersion,
              isPurchase: true,
            }
          ),
          isLeaseDisabled
            ? Promise.reject(new Error('Lease disabled'))
            : getMultiMonthlyPayments(
                {
                  offer: digitalRetailOffers[LEASE],
                  zipCode: storedZipCode,
                  selectedTerm: leaseCalculatorValues.term,
                  selectedTradeIn: leaseCalculatorValues.tradeIn,
                  selectedMileage: leaseCalculatorValues.annualMileage,
                  selectedDownPayment: leaseCalculatorValues.downPayment,
                  prices,
                  vehicle: vinData,
                  vehiclePrices: getVehiclePricesNewFields(vinData),
                },
                {
                  visitorId,
                  pageName,
                  venomVersion,
                  isPurchase: false,
                }
              ),
        ]);

        const { value: purchasePayments, reason: purchaseError } = purchasePaymentsResponse;
        const { value: leasePayments, reason: leaseError } = leasePaymentsResponse;

        const dealsByType = getDealsByType({
          vehicle: vinData,
          photoUrl,
          purchasePayments,
          leasePayments,
          calculatorSelections,
          primaryIncentives: primaryLoanIncentives,
          selectedIncentives: isLeasePaymentFocused ? selectedLeaseIncentives : selectedLoanIncentives,
          isLeasePaymentFocused,
        });

        this.setErrorMessage({
          field: CALCULATION_API_ERROR_FIELD,
          error: purchaseError,
          clear: !purchaseError && !!purchasePayments,
          isPurchase: true,
        });
        this.setErrorMessage({
          field: CALCULATION_API_ERROR_FIELD,
          error: leaseError,
          clear: !leaseError && !!leasePayments,
          isPurchase: false,
        });

        this.setState({
          deals: {
            [PURCHASE]: purchaseError
              ? []
              : dealsByType[PURCHASE].filter(deal => get(deal, 'dealOffer.dealType') !== 'msrp'),
            [LEASE]: leaseError ? [] : dealsByType[LEASE].filter(deal => get(deal, 'dealOffer.dealType') !== 'msrp'),
          },
          basePurchaseDeal: purchaseError
            ? {}
            : find(dealsByType[PURCHASE], deal => get(deal, 'dealOffer.dealType') === 'msrp'),
        });
      } catch (error) {
        this.setState({ deals: { [PURCHASE]: [], [LEASE]: [] }, basePurchaseDeal: {} });

        this.setErrorMessage({ field: CALCULATION_API_ERROR_FIELD, error, isPurchase: true });
        this.setErrorMessage({ field: CALCULATION_API_ERROR_FIELD, error, isPurchase: false });
      } finally {
        this.togglePendingState(false);
      }
    };

    render() {
      const { deals, basePurchaseDeal, calculatorConfigs } = this.state;
      const { isPurchase, vinData } = this.props;

      return (
        <WrappedComponent
          {...omit(this.props, [
            'selectedLeaseIncentives',
            'selectedLoanIncentives',
            'primaryLoanIncentives',
            'primaryLeaseIncentives',
            'conditionalLoanIncentives',
            'conditionalLeaseIncentives',
          ])}
          deals={isTmvPriceAvailable(vinData) ? deals : this.getDealsFromTmv()}
          baseDeal={basePurchaseDeal}
          calculatorConfigs={calculatorConfigs}
          setErrorMessage={this.setErrorMessage}
          getErrorMessage={this.getErrorMessage}
          togglePendingState={this.togglePendingState}
          isPurchase={isPurchase}
          isSubmitted={this.getIsSubmitted()}
          isUsed={this.getIsUsed()}
        />
      );
    }
  }

  const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  CalculateDealPaymentByType.displayName = `calculateDealPaymentsByType(${wrappedComponentName})`;

  return CalculateDealPaymentByType;
}

export const mapStateToProps = state => ({
  defaultLoanTerm72mo: !!state.featureFlags?.defaultLoanTerm72mo,
});

const stateToPropsConfig = {
  // user preferences
  storedZipCode: bindToPath(PATHS.STORED_ZIP_CODE, VisitorModel),
  storedLeaseSelections: bindToPath(getStoredSelectionsPath({ isPurchase: false }), CalculatorsModel, null, false),
  storedLoanSelections: bindToPath(getStoredSelectionsPath({ isPurchase: true }), CalculatorsModel, null, false),
  // system
  visitorId: bindToPath('id', VisitorModel),
  // data
  photoUrl: bindToPath(PATHS.PHOTO_URL, CarBuyingModel),
  totalWithOptionsTMV: bindToPath(({ vinData, customConfig }) => {
    const styleId = vinData?.vehicleInfo?.styleInfo?.styleId;

    const tmvAPIoptions = get(customConfig, 'tmvAPIoptions');
    const dealerZip = get(tmvAPIoptions, 'dealerZip');
    const colorId = get(tmvAPIoptions, 'colorId');
    const optionIds = get(tmvAPIoptions, 'optionIds');
    return !isTmvPriceAvailable(vinData) && !!styleId && !!dealerZip
      ? buildConfigurationTmvSimplifiedWithOptionsPath(styleId, dealerZip, colorId, optionIds)
      : null;
  }, BuildPriceModel),
  protocol: bindToPath('protocol', RequestModel),
  hostName: bindToPath('hostName', RequestModel),
  url: bindToPath('url', RequestModel),
};

export const connectedCalculateDealPaymentsByType = flow(
  calculateDealPaymentsByType,
  withIncentives,
  component => connectToModel(component, stateToPropsConfig),
  component =>
    connectToModel(component, {
      vinData: bindToPath(PATHS.VEHICLE, InventoryModel, null, false),
    }),
  withMetrics,
  connect(mapStateToProps)
);
